import { gql, MutationResult, useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { removetargetListFragment } from 'basics/graphql/fragments/targetList.fragments';
import { MutationReturnType } from 'basics/types/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

type UseRemoveTargetsByMdmIdListMutationResponse = {
  removeTargetsByMdmIdList: {
    deletedCount: number;
  };
};

type UseRemoveTargetsByMdmIdListMutation = [
  (mdmIdList?: string[], cepId?: string) => Promise<MutationReturnType<number>>,
  MutationResult<UseRemoveTargetsByMdmIdListMutationResponse>,
];

export const removeTargetsByMdmIdListMutationCall = gql`
  mutation removeTargetsByMdmIdList($mdmIdList: [String!]!, $cepId: String!, $sendToVeeva: Boolean!) {
    removeTargetsByMdmIdList(mdmIdList: $mdmIdList, cepId: $cepId, sendToVeeva: $sendToVeeva) {
      ${removetargetListFragment}
    }
  }
`;

export const useRemoveTargetsByMdmIdListMutation = (): UseRemoveTargetsByMdmIdListMutation => {
  const [removeTargetsByMdmIdList, state] = useMutation<UseRemoveTargetsByMdmIdListMutationResponse>(removeTargetsByMdmIdListMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const flags = useFlags();

  const executeMutation = useCallback(async (mdmIdList, cepId) => {
    try {
      const { data } = await removeTargetsByMdmIdList({
        variables: {
          mdmIdList,
          cepId,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816,
        },
      });

      return { data: data?.removeTargetsByMdmIdList.deletedCount || 0, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'removeTargetsByMdmIdList.ts - useRemoveTargetsByMdmIdListMutation',
        });

        return { data: null, error: customContext.message as string };
      }

      return { data: null, error: null };
    }
  }, [flags.mayaEnableVeevaBiogenlinc1816, removeTargetsByMdmIdList]);

  return [executeMutation, state];
};
